import { FC, memo, ReactNode, useEffect } from 'react'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { nextOccurrenceFrom } from './NextOccurrence'
import dayjs, { Dayjs } from 'dayjs'
import { useScheduledReviews } from 'hooks/queries/scheduledReviews/useScheduledReviews'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useSelector } from '@xstate/react'
import { useReviewsWizard } from '../ReviewsWizardProvider'
import { ReviewAssetType } from '../types'
import { ArticleFlat, ScheduledReview } from '../../../types'

export interface ConflictResolution {
  latestEnd?: Dayjs
}

interface WithChildren {
  children(conflictResolution: ConflictResolution): ReactNode
}

interface WithArticle {
  articleId: string
}

interface Props {
  onConflict: (conflictResolution: Required<ConflictResolution>) => void
}

const NoConflicts = ({ children }: WithChildren) => <>{children({})}</>

const AvoidArticleConflicts: FC<Required<Props & WithChildren> & WithScheduledReview> = ({
  onConflict,
  scheduledReview,
  children,
}) => {
  const { service } = useReviewsWizard()
  const endDate = useSelector(service, (state) => state.context.endDate)
  const interval = useSelector(service, (state) => state.context.interval)
  const weekdays = useSelector(service, (state) => state.context.weekdays)

  const maxDate = scheduledReview?.next_occurrence
  const nextOccurrence = nextOccurrenceFrom(undefined, interval, weekdays)
  let latestEnd = maxDate ? dayjs(maxDate) : undefined

  if (nextOccurrence) {
    latestEnd = latestEnd ? dayjs.min(latestEnd, dayjs(nextOccurrence)) : dayjs(nextOccurrence)
  }

  useEffect(() => {
    if (latestEnd && latestEnd.isBefore(dayjs(endDate))) {
      onConflict({ latestEnd })
    }
  }, [latestEnd, endDate, onConflict])

  return <>{children({ latestEnd })}</>
}

const AvoidConflicts: FC<Props & WithChildren> = ({ children, onConflict }) => {
  const { service } = useReviewsWizard()
  const asset = useSelector(service, (state) => state.context.asset)
  const assetType = useSelector(service, (state) => state.context.assetType)

  if (assetType === ReviewAssetType.Article && asset) {
    return (
      <NextScheduledArticleReview articleId={(asset as ArticleFlat).id}>
        {({ scheduledReview }) => (
          <AvoidArticleConflicts onConflict={onConflict} scheduledReview={scheduledReview}>
            {children}
          </AvoidArticleConflicts>
        )}
      </NextScheduledArticleReview>
    )
  }

  return <NoConflicts>{children}</NoConflicts>
}

export const NextScheduledReview = ({ children }: WithNextReview) => {
  const { service } = useReviewsWizard()
  const asset = useSelector(service, (state) => state.context.asset)
  const assetType = useSelector(service, (state) => state.context.assetType)

  if (assetType === ReviewAssetType.Article && asset) {
    return <NextScheduledArticleReview articleId={(asset as ArticleFlat).id}>{children}</NextScheduledArticleReview>
  }

  return <NoScheduledReviews>{children as any}</NoScheduledReviews>
}

interface WithNextReview {
  children(review: WithScheduledReview & { isLoading: boolean }): ReactNode
}

interface WithScheduledReview {
  scheduledReview?: ScheduledReview
}

const NextScheduledArticleReview = ({ articleId, children }: WithArticle & WithNextReview) => {
  const workspaceId = useCurrentWorkspaceId()
  const { data, isLoading } = useScheduledReviews({
    workspaceId,
    nextOccurrencegt: dayjs().startOf('hour').format(),
    items: 1,
    article: articleId,
  })

  let scheduledReview: ScheduledReview | undefined = undefined
  const successfulPageRequests = (data?.pages || []).filter(
    (page) => page.type === SUCCESS_RESPONSE_TYPE && page.data.scheduled_reviews,
  )

  if (successfulPageRequests.length > 0 && successfulPageRequests[0].data.scheduled_reviews.length > 0) {
    scheduledReview = successfulPageRequests[0].data.scheduled_reviews[0]
  }

  return <>{children({ scheduledReview, isLoading })}</>
}

const NoScheduledReviews = ({ children }: WithNextReview) => <>{children({ isLoading: false })}</>

const MemoizedAvoidConflicts = memo(AvoidConflicts)

export { MemoizedAvoidConflicts as AvoidConflicts }
